/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import SubjectTypesEnum from '@linc-technologies/linced-common/enums/subject-type';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Category from 'linced-admin/models/goals/category';
import type School from 'linced-admin/models/school';
import type UsageSet from 'linced-admin/models/schools/usage-set';

// Proto: goals.GoalSubjectTemplate.
export default class Subject extends Model {
  declare [Type]: 'goals/subject';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string')
  declare campusId: string | undefined; // TODO: implement when we have Campuses

  @attr('boolean', { defaultValue: false })
  declare isPublished: boolean;

  @hasMany('goals/category', { async: true, inverse: 'subjectId' })
  declare categories: AsyncHasMany<Category>;

  @computed('categories.content.[]')
  get categoriesArray() {
    return resolved(this.categories);
  }

  categorySortOrder = Object.freeze(['sequence', 'label']);

  @sort('categoriesArray', 'categorySortOrder')
  declare sortedCategories: unknown[];

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  // Usage sets membership
  @hasMany('schools/usage-set', { async: true, inverse: 'goalSubjectIds' })
  declare usageSetIds: AsyncHasMany<UsageSet>;

  @computed('usageSetIds.content.[]')
  get usageSetsArray() {
    return resolved(this.usageSetIds);
  }

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string')
  declare templateId: string | undefined; // TODO: implement once Templates are added

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('boolean', { defaultValue: false })
  declare trackable: boolean;

  @attr('boolean', { defaultValue: false })
  declare calculateBestFits: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToTeachers: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToParents: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToLearners: boolean;

  @attr('number', { defaultValue: 0 })
  declare reportVisibility: number;

  @attr('boolean', { defaultValue: false })
  declare showRadialDials: boolean;

  @attr('boolean', { defaultValue: false })
  declare isGradable: boolean;

  @attr('boolean', { defaultValue: false })
  declare isTopic: boolean;

  @attr('number', { defaultValue: 0 })
  declare subjectType: number;

  @attr('string', { defaultValue: '' })
  declare SC1: string;

  @attr('string', { defaultValue: '' })
  declare SC2: string;

  @attr('string', { defaultValue: '' })
  declare SC3: string;

  @attr('string', { defaultValue: '' })
  declare SC4: string;

  @attr('string', { defaultValue: '' })
  declare SC5: string;

  // onTrackInterimGrade / onTrackFullGrade are the minimum Progression
  // judgements required to count as on track for an interim
  // (Milestone.isInterim) and a full Milestone respectively. The API's Grade
  // enum aligns exactly with progression-judgement, so these compare directly
  // against a Progression's judgement.
  @attr('number', { defaultValue: 0 })
  declare onTrackInterimGrade: number;

  @attr('number', { defaultValue: 0 })
  declare onTrackFullGrade: number;

  @computed('subjectType')
  get subjectTypeName() {
    const subjectType = this.subjectType;
    // Check if subjectType is valid, otherwise default to "Not set"
    return subjectType
      ? SubjectTypesEnum.getLabel(subjectType) || 'Not set'
      : 'Not set';
  }
}
